import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {Renderer, RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  BoxedInputInheritableProps,
  StyledRequiredSpan,
  StyledTextLabel,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {boxedInputColorReaders} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';

/*
 * Constants.
 */

const DEFAULT_VALUES_HEIGHT = 20; // px

/*
 * Props.
 */

export interface LabeledInputDropdownProps {
  label: BoxedInputInheritableProps['label'];
  /** Used as dropdown box value when the input is empty. */
  renderPlaceholder: Renderer;
  /** Used as dropdown box value when the input is not empty. */
  renderValue: Renderer;
  /** The dropdown to render on click */
  renderDropdown: RendererOf<PopoverTransferProps>;
  isDisabled: BoxedInputInheritableProps['isDisabled'];
  isRequired: BoxedInputInheritableProps['isRequired'];
  messageForDisabled: ReactNode;
  hasError?: boolean;
  errorMessage: ReactNode;
  hasPreviousSelection: boolean;
}

/*
 * Style.
 */

const StyledFadedDiv = styled.div<{$isActive: boolean}>`
  color: ${(p) => (p.$isActive ? p.theme.greys.shade90 : p.theme.greys.shade80)};
  flex: 1;
  ${ellipsis()};
`;

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
  height: ${DEFAULT_VALUES_HEIGHT}px;
`;

const StyledPlaceholder = styled.span`
  font-weight: ${fontWeights.normal};
  color: ${boxedInputColorReaders.textPlaceholder};
  height: ${DEFAULT_VALUES_HEIGHT}px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/*
 * Component.
 */

/** A form input component for selecting from a custom dropdown. */
export const LabeledInputDropdown = (props: LabeledInputDropdownProps) => {
  const {
    hasPreviousSelection,
    isDisabled,
    isRequired,
    label,
    renderPlaceholder,
    renderValue,
    messageForDisabled,
    renderDropdown,
  } = props;

  const maybeRenderTooltip = () => {
    if (!isDisabled && messageForDisabled) {
      return undefined;
    }

    return messageForDisabled;
  };

  const value = hasPreviousSelection ? (
    <StyledValueDiv>
      <StyledFadedDiv $isActive={!isDisabled}>{renderValue()}</StyledFadedDiv>
    </StyledValueDiv>
  ) : (
    <StyledPlaceholder>{renderPlaceholder()}</StyledPlaceholder>
  );

  return (
    <StyledWrapperDiv>
      {label && (
        <StyledTextLabel $size={VisualSizesEnum.MEDIUM} $isDisabled={isDisabled}>
          {label}
          {isRequired && <StyledRequiredSpan>*</StyledRequiredSpan>}
        </StyledTextLabel>
      )}
      <DropdownBoxCoordinator
        renderDropdown={renderDropdown}
        renderTooltip={maybeRenderTooltip}
        value={value}
        shouldDisablePadding
        isDisabled={isDisabled}
        isRequired={isRequired}
        hasError={props.hasError}
        errorMessage={props.errorMessage}
      />
    </StyledWrapperDiv>
  );
};
